/**
 * HealthBar - Displays player health status
 */

import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../state/store';

interface HealthBarProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  showValue?: boolean;
}

export const HealthBar: React.FC<HealthBarProps> = ({
  position = 'top-left',
  showValue = true
}) => {
  const health = useSelector((state: RootState) => state.player.health);
  const maxHealth = useSelector((state: RootState) => state.player.maxHealth);
  const prevHealthRef = useRef(health);
  const [isDamaged, setIsDamaged] = useState(false);

  useEffect(() => {
    if (health < prevHealthRef.current) {
      setIsDamaged(true);
      const t = setTimeout(() => setIsDamaged(false), 500);
      prevHealthRef.current = health;
      return () => clearTimeout(t);
    }
    prevHealthRef.current = health;
    return undefined;
  }, [health]);

  const healthPercentage = (health / maxHealth) * 100;

  // Determine color based on health percentage
  const getHealthColor = (): string => {
    if (healthPercentage > 60) return '#00ff00'; // Green
    if (healthPercentage > 30) return '#ffaa00'; // Orange
    return '#ff0000'; // Red
  };

  const positionStyles = {
    'top-left': { top: '20px', left: '20px' },
    'top-right': { top: '20px', right: '20px' },
    'bottom-left': { bottom: '20px', left: '20px' },
    'bottom-right': { bottom: '20px', right: '20px' },
  };

  return (
    <div style={{
      position: 'absolute',
      ...positionStyles[position],
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      fontFamily: 'monospace',
      color: '#ffffff',
      pointerEvents: 'none',
      userSelect: 'none',
    }}>
      <style>{`
        @keyframes healthDamagePulse {
          0%   { box-shadow: 0 0 0 0 rgba(255,0,0,0.9); border-color: #ff0000; }
          50%  { box-shadow: 0 0 10px 4px rgba(255,0,0,0.6); border-color: #ff4444; }
          100% { box-shadow: 0 0 0 0 rgba(255,0,0,0); border-color: #444; }
        }
      `}</style>
      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>HEALTH</div>
      <div style={{
        width: '200px',
        height: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        border: '2px solid #444',
        borderRadius: '4px',
        overflow: 'hidden',
        position: 'relative',
        animation: isDamaged ? 'healthDamagePulse 0.5s ease-out' : 'none',
      }}>
        {/* Health bar fill */}
        <div style={{
          width: `${healthPercentage}%`,
          height: '100%',
          backgroundColor: getHealthColor(),
          transition: 'width 0.3s ease, background-color 0.3s ease',
        }} />

        {/* Health value text */}
        {showValue && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#ffffff',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
          }}>
            {health} / {maxHealth}
          </div>
        )}
      </div>
    </div>
  );
};
